import React from "react";
import { WorkflowMode } from "../../common/enums";
import { BaseFullScreenTakeover } from "../../Components/BaseFullScreenTakeover";
import { PaymentPrefConfirmation } from "../../Components/PaymentPrefConfirmation";
import { PositionSingleDisplay } from "../../Components/PositionSingleDisplay";
import { SelectPositionsToUpdate } from "../../Components/SelectPositionsToUpdate";
import { AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import { PaymentPrefCreationForPositionWorkflow } from "../PaymentPrefCreationForPositionWorkflow";
import { PaymentPrefEditForAccountAndPositionWorkflow } from "../PaymentPrefEditForAccountAndPositionWorkflow";
import { PaymentPrefForPositionWorkflowProps } from "./types";

/**
 * A workflow component that orchestrates the creation or editing of payment preferences for a specific position.
 * It acts as a router between creation and editing workflows, rendering the appropriate component based on the workflow mode.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {() => void} props.onExit - Function called when the workflow is exited
 * @param {() => void} props.onSuccess - Function called when the workflow completes successfully
 * @param {WorkflowMode} props.workflowMode - Mode of the workflow (Create or Edit)
 * @param {Object} props.workflowParams - Parameters specific to the workflow mode
 * @param {Position} props.workflowParams.position - Position details for the workflow
 * @param {string} [props.workflowParams.sourcePaymentPrefId] - ID of source payment preference (for Create mode)
 * @param {ExistingPaymentPref} [props.workflowParams.existingPaymentPref] - Existing payment preference (for Edit mode)
 * @param {string} [props.accountName] - Name of the account (required for GP flows with staff approvals)
 * @param {Arena} [props.arena] - Arena configuration object
 *
 * @example
 * ```tsx
 * // Create Mode
 * <PaymentPrefForPositionWorkflow
 *   onExit={() => setShowWorkflow(false)}
 *   onSuccess={handleSuccess}
 *   workflowMode={WorkflowMode.Create}
 *   workflowParams={{
 *     position: { id: '123', accountId: '456' },
 *     sourcePaymentPrefId: '789'
 *   }}
 *   arena={{ features: { hasPositionPaymentPrefStaffApprovals: true }}}
 * />
 *
 * // Edit Mode
 * <PaymentPrefForPositionWorkflow
 *   onExit={() => setShowWorkflow(false)}
 *   onSuccess={handleSuccess}
 *   workflowMode={WorkflowMode.Edit}
 *   workflowParams={{
 *     position: { id: '123', accountId: '456' },
 *     existingPaymentPref: { id: '789', paymentMethod: 'ACH' }
 *   }}
 *   arena={{ features: { hasPositionPaymentPrefStaffApprovals: true }}}
 * />
 * ```
 *
 * @remarks
 * - Uses BaseFullScreenTakeover for modal-like presentation
 * - Routes to PaymentPrefCreationForPositionWorkflow for Create mode
 * - Routes to PaymentPrefEditForAccountAndPositionWorkflow for Edit mode
 * - Throws error for unsupported workflow modes
 */

export const PaymentPrefForPositionWorkflow: React.FC<PaymentPrefForPositionWorkflowProps> = ({
  onExit,
  onSuccess,
  workflowMode,
  workflowParams,
  accountName,
  arena,
}) => {
  if (workflowMode === WorkflowMode.Create) {
    return (
      <BaseFullScreenTakeover onExit={onExit} title="Update payment instructions">
        <PaymentPrefCreationForPositionWorkflow
          position={workflowParams.position}
          sourcePaymentPrefId={workflowParams.sourcePaymentPrefId}
          onExit={onExit}
          onSuccess={onSuccess}
          arena={arena}
        />
      </BaseFullScreenTakeover>
    );
  } else if (workflowMode === WorkflowMode.Edit) {
    return (
      <BaseFullScreenTakeover onExit={onExit} title="Update payment instructions">
        <PaymentPrefEditForAccountAndPositionWorkflow
          ListOfPositionsToUpdateComponent={PositionSingleDisplay}
          position={workflowParams.position}
          existingPaymentPref={workflowParams.existingPaymentPref}
          initialAssociatePaymentPref={AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition}
          onExit={onExit}
          onSuccess={onSuccess}
          suffixComponents={[SelectPositionsToUpdate, PaymentPrefConfirmation]}
          accountName={accountName}
          arena={arena}
        />
      </BaseFullScreenTakeover>
    );
  }

  /**
   * If the workflow mode is not supported, throw an error.
   */
  throw new Error("Unsupported workflow mode");
};

export type { PaymentPrefForPositionWorkflowProps };
